import { supabase } from '@/integrations/supabase/client';

export interface SourcePerformanceMetrics {
  sourceId: string;
  sourceName: string;
  sourceType: 'supplier' | 'agent' | 'farm' | 'harvest';
  orderCount: number;
  totalVolume: number;
  totalSpend: number;
  avgOrderValue: number;
  frequencyPerMonth: number;
  completionRate: number;
  rejectionRate: number;
}

export interface SourcePerformanceSummary {
  totalSources: number;
  totalVolume: number;
  totalSpend: number;
  totalOrders: number;
  avgFrequency: number;
}

interface RawProcurementRow {
  source_type: string;
  source_id: string | null;
  status: string;
  total_quantity: number | null;
  total_amount: number | null;
  created_at: string;
  coordinator_id: string | null;
}

export async function fetchSourcePerformance(
  startDate: string,
  endDate: string,
  sourceTypeFilter?: string
): Promise<{ metrics: SourcePerformanceMetrics[]; summary: SourcePerformanceSummary }> {
  // Fetch all procurements in range
  let query = supabase
    .from('procurements')
    .select('source_type, source_id, status, total_quantity, total_amount, created_at, coordinator_id')
    .gte('created_at', startDate)
    .lte('created_at', endDate);

  if (sourceTypeFilter && sourceTypeFilter !== 'all') {
    query = query.eq('source_type', sourceTypeFilter);
  }

  const { data: procurements, error } = await query;
  if (error) throw error;
  if (!procurements || procurements.length === 0) {
    return { metrics: [], summary: { totalSources: 0, totalVolume: 0, totalSpend: 0, totalOrders: 0, avgFrequency: 0 } };
  }

  // Group by source_type + source_id (use coordinator_id for harvest type)
  const grouped = new Map<string, { rows: RawProcurementRow[]; sourceType: string; sourceId: string }>();

  for (const row of procurements as RawProcurementRow[]) {
    const effectiveId = row.source_type === 'harvest'
      ? (row.coordinator_id || 'unknown-harvest')
      : (row.source_id || 'unknown');
    const key = `${row.source_type}::${effectiveId}`;
    if (!grouped.has(key)) {
      grouped.set(key, { rows: [], sourceType: row.source_type, sourceId: effectiveId });
    }
    grouped.get(key)!.rows.push(row);
  }

  // Collect IDs for batch name resolution
  const supplierIds = new Set<string>();
  const agentIds = new Set<string>();
  const farmIds = new Set<string>();
  const coordinatorIds = new Set<string>();

  for (const g of grouped.values()) {
    if (g.sourceType === 'supplier') supplierIds.add(g.sourceId);
    else if (g.sourceType === 'agent') agentIds.add(g.sourceId);
    else if (g.sourceType === 'farm') farmIds.add(g.sourceId);
    else if (g.sourceType === 'harvest') coordinatorIds.add(g.sourceId);
  }

  // Batch lookups in parallel
  const [suppliersRes, agentsRes, farmsRes, coordinatorsRes] = await Promise.all([
    supplierIds.size > 0
      ? supabase.from('suppliers').select('id, name').in('id', [...supplierIds])
      : Promise.resolve({ data: [] }),
    agentIds.size > 0
      ? supabase.from('agents').select('id, name').in('id', [...agentIds])
      : Promise.resolve({ data: [] }),
    farmIds.size > 0
      ? supabase.from('farms').select('id, farmer_name').in('id', [...farmIds])
      : Promise.resolve({ data: [] }),
    coordinatorIds.size > 0
      ? supabase.from('user_profiles').select('id, first_name, last_name').in('id', [...coordinatorIds])
      : Promise.resolve({ data: [] }),
  ]);

  const nameMap = new Map<string, string>();
  (suppliersRes.data || []).forEach((s: any) => nameMap.set(`supplier::${s.id}`, s.name));
  (agentsRes.data || []).forEach((a: any) => nameMap.set(`agent::${a.id}`, a.name));
  (farmsRes.data || []).forEach((f: any) => nameMap.set(`farm::${f.id}`, f.farmer_name));
  (coordinatorsRes.data || []).forEach((c: any) =>
    nameMap.set(`harvest::${c.id}`, [c.first_name, c.last_name].filter(Boolean).join(' ') || 'Coordinator')
  );

  // Calculate date span in months
  const msPerMonth = 30.44 * 24 * 60 * 60 * 1000;
  const spanMonths = Math.max(1, (new Date(endDate).getTime() - new Date(startDate).getTime()) / msPerMonth);

  // Build metrics
  const metrics: SourcePerformanceMetrics[] = [];
  for (const [key, g] of grouped) {
    const rows = g.rows;
    const totalOrders = rows.length;
    const totalVolume = rows.reduce((s, r) => s + (r.total_quantity || 0), 0);
    const totalSpend = rows.reduce((s, r) => s + (r.total_amount || 0), 0);
    const completedCount = rows.filter(r => ['delivered', 'paid', 'completed', 'warehouse_approved'].includes(r.status)).length;
    const rejectedCount = rows.filter(r => ['rejected', 'finance_rejected', 'warehouse_rejected'].includes(r.status)).length;

    metrics.push({
      sourceId: g.sourceId,
      sourceName: nameMap.get(key) || g.sourceId,
      sourceType: g.sourceType as SourcePerformanceMetrics['sourceType'],
      orderCount: totalOrders,
      totalVolume,
      totalSpend,
      avgOrderValue: totalOrders > 0 ? totalSpend / totalOrders : 0,
      frequencyPerMonth: totalOrders / spanMonths,
      completionRate: totalOrders > 0 ? (completedCount / totalOrders) * 100 : 0,
      rejectionRate: totalOrders > 0 ? (rejectedCount / totalOrders) * 100 : 0,
    });
  }

  // Sort by total spend descending
  metrics.sort((a, b) => b.totalSpend - a.totalSpend);

  const summary: SourcePerformanceSummary = {
    totalSources: metrics.length,
    totalVolume: metrics.reduce((s, m) => s + m.totalVolume, 0),
    totalSpend: metrics.reduce((s, m) => s + m.totalSpend, 0),
    totalOrders: metrics.reduce((s, m) => s + m.orderCount, 0),
    avgFrequency: metrics.length > 0
      ? metrics.reduce((s, m) => s + m.frequencyPerMonth, 0) / metrics.length
      : 0,
  };

  return { metrics, summary };
}
